// ** React Imports
import { createContext, useState, ReactNode, useEffect } from 'react';

export type PaletteMode = 'light' | 'dark';
export type Direction = 'ltr' | 'rtl';

// ** ThemeConfig Import
import themeConfig from 'src/configs/themeConfig';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { useThemeStore } from 'src/@core/stores/useThemeStore';
// ** Types Import
import {
  Skin,
  AppBar,
  Footer,
  ThemeColor,
  ContentWidth,
  VerticalNavToggle,
} from 'src/@core/layouts/types';
import { useApp } from 'src/@carketa/app/AppContext';
import { USER_TYPE } from 'src/server/common/permissions';
import { hasVelocifi } from 'src/utils/velocifi/velocifi';

export type Settings = {
  skin: Skin;
  appBar?: AppBar;
  footer?: Footer;
  mode: PaletteMode;
  navHidden?: boolean; // navigation menu
  appBarBlur: boolean;
  direction: Direction;
  navCollapsed: boolean;
  themeColor: ThemeColor;
  contentWidth: ContentWidth;
  layout?: 'vertical' | 'horizontal';
  lastLayout?: 'vertical' | 'horizontal';
  verticalNavToggleType: VerticalNavToggle;
  toastPosition?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
};

export type PageSpecificSettings = {
  skin?: Skin;
  appBar?: AppBar;
  footer?: Footer;
  mode?: PaletteMode;
  navHidden?: boolean; // navigation menu
  appBarBlur?: boolean;
  direction?: Direction;
  navCollapsed?: boolean;
  themeColor?: ThemeColor;
  contentWidth?: ContentWidth;
  layout?: 'vertical' | 'horizontal';
  lastLayout?: 'vertical' | 'horizontal';
  verticalNavToggleType?: VerticalNavToggle;
  toastPosition?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
};
export type SettingsContextValue = {
  settings: Settings;
  saveSettings: (updatedSettings: Settings) => void;
};

interface SettingsProviderProps {
  children: ReactNode;
  pageSettings?: PageSpecificSettings | void;
}

const initialSettings: Settings = {
  themeColor: 'success',
  mode: themeConfig.mode,
  footer: themeConfig.footer,
  layout: themeConfig.layout,
  lastLayout: themeConfig.layout,
  direction: themeConfig.direction,
  navHidden: themeConfig.navHidden,
  appBarBlur: themeConfig.appBarBlur,
  navCollapsed: themeConfig.navCollapsed,
  contentWidth: themeConfig.contentWidth,
  toastPosition: themeConfig.toastPosition,
  verticalNavToggleType: themeConfig.verticalNavToggleType,
  skin:
    themeConfig.layout === 'horizontal' && themeConfig.skin === 'semi-dark'
      ? 'default'
      : themeConfig.skin,
  appBar:
    themeConfig.layout === 'horizontal' && themeConfig.appBar === 'hidden'
      ? 'fixed'
      : themeConfig.appBar,
};

const staticSettings = {
  appBar: initialSettings.appBar,
  footer: initialSettings.footer,
  layout: initialSettings.layout,
  navHidden: initialSettings.navHidden,
  lastLayout: initialSettings.lastLayout,
  toastPosition: initialSettings.toastPosition,
};

const restoreSettings = (isVelocifi: boolean): Settings | null => {
  let settings = null;

  try {
    const storedData: string | null = window.localStorage.getItem('settings');

    if (storedData) {
      settings = { ...JSON.parse(storedData), ...staticSettings };
    } else {
      settings = initialSettings;
    }
  } catch (err) {
    console.error(err);
  }
  settings.themeColor = 'success';

  if (isVelocifi) {
    settings.themeColor = 'velocifi';
  }

  const hasKsl = window.location.pathname.includes('ksl-dealer-insights');
  if (hasKsl) {
    settings.themeColor = 'ksl';
  }

  return settings;
};

// set settings in localStorage
const storeSettings = (settings: Settings) => {
  const initSettings = Object.assign({}, settings);

  delete initSettings.appBar;
  delete initSettings.footer;
  delete initSettings.layout;
  delete initSettings.navHidden;
  delete initSettings.lastLayout;
  delete initSettings.toastPosition;
  window.localStorage.setItem('settings', JSON.stringify(initSettings));

  if (window.flutter_inappwebview) {
    return window.flutter_inappwebview.callHandler(
      'settingsChangeHandler',
      initSettings
    );
  }
};

// ** Create Context
export const SettingsContext = createContext<SettingsContextValue>({
  saveSettings: () => null,
  settings: initialSettings,
});

export const SettingsProvider = ({
  children,
  pageSettings,
}: SettingsProviderProps) => {
  const { me } = useApp();

  const {
    isFeatureEnabled: shadcn_dark_mode,
    isInitialLoading: shadcn_dark_mode_loading,
    isLoadingMe: shadcn_dark_mode_loading_me,
  } = useFeatureFlag('shadcn-dark-mode');

  const setShadcnTheme = useThemeStore((s) => s.setTheme);

  // ** State
  const [settings, setSettings] = useState<Settings>({ ...initialSettings });

  useEffect(() => {
    const restoredSettings = restoreSettings(
      hasVelocifi() || me?.type === USER_TYPE.agent
    );

    if (
      restoredSettings &&
      !shadcn_dark_mode &&
      !shadcn_dark_mode_loading &&
      !shadcn_dark_mode_loading_me
    ) {
      setShadcnTheme('light');
      storeSettings({ ...restoredSettings, mode: 'light' });
      restoredSettings.mode = 'light';
    }

    if (restoredSettings) {
      setSettings({ ...restoredSettings });
    }
    if (pageSettings) {
      setSettings({ ...settings, ...pageSettings });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageSettings,
    me,
    shadcn_dark_mode,
    shadcn_dark_mode_loading,
    shadcn_dark_mode_loading_me,
    setShadcnTheme,
    storeSettings,
  ]);

  useEffect(() => {
    if (settings.layout === 'horizontal' && settings.skin === 'semi-dark') {
      saveSettings({ ...settings, skin: 'default' });
    }
    if (settings.layout === 'horizontal' && settings.appBar === 'hidden') {
      saveSettings({ ...settings, appBar: 'fixed' });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.layout]);

  const saveSettings = (updatedSettings: Settings) => {
    storeSettings(updatedSettings);
    setSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider value={{ settings, saveSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;
